import React, { useEffect, useState } from "react";
import { fetchDashboard, DashboardData } from "./dashboardApi";
import styles from "./Dashboard.module.css";
import {
  Music,
  Users,
  MessageSquare,
  RefreshCw,
  TrendingUp,
  Heart,
  Play,
  Sparkles,
  ArrowUp,
  AlertCircle,
} from "lucide-react";

export default function DashboardPage() {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await fetchDashboard({
        recent_limit: 5,
        popular_limit: 5,
        growth_days: 30,
      });
      setDashboard(data);
    } catch (error: any) {
      console.error("Failed to load dashboard:", error);
      setError(error.response?.data?.message || "Dashboard yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const formatDurationMs = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          <p className={styles.loadingText}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.errorContainer}>
            <AlertCircle size={48} className={styles.errorIcon} />
            <h3 className={styles.errorTitle}>Error Loading Dashboard</h3>
            <p className={styles.errorMessage}>{error}</p>
            <button onClick={loadDashboard} className={styles.errorButton}>
              <RefreshCw size={16} />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboard) return null;

  const stats = [
    {
      icon: Users,
      value: dashboard.overview.total_users.toLocaleString(),
      label: "Total Users",
      growth: dashboard.growth_metrics?.new_users ? `+${dashboard.growth_metrics.new_users}` : null,
      bgClass: styles.iconBgBlue,
      iconClass: styles.iconBlue,
      gradientClass: styles.gradientBlue,
    },
    {
      icon: Music,
      value: dashboard.overview.total_songsv2.toLocaleString(),
      label: "Total Songs",
      growth: dashboard.growth_metrics?.new_songs_v2 ? `+${dashboard.growth_metrics.new_songs_v2}` : null,
      bgClass: styles.iconBgPurple,
      iconClass: styles.iconPurple,
      gradientClass: styles.gradientPurple,
    },
    {
      icon: MessageSquare,
      value: dashboard.overview.total_song_requests.toLocaleString(),
      label: "Song Requests",
      bgClass: styles.iconBgAmber,
      iconClass: styles.iconAmber,
      gradientClass: styles.gradientAmber,
    },
  ];

  const musicStats = [
    {
      icon: TrendingUp,
      value: dashboard.songs_statistics.songs_v2.avg_bpm?.toFixed(0) || "0",
      label: "Avg BPM",
    },
    {
      icon: Play,
      value: dashboard.songs_statistics.songs_v2.total_plays.toLocaleString(),
      label: "Total Plays",
    },
    {
      icon: Heart,
      value: dashboard.songs_statistics.songs_v2.total_likes.toLocaleString(),
      label: "Total Likes",
    },
  ];

  return (
    <div className={styles.container}>
      <div style={{ maxWidth: '90rem', margin: '0 auto' }}>
        {/* Header */}
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <h1 className={styles.title}>
              <Sparkles size={32} className={styles.titleIcon} />
              Dashboard
            </h1>
            <p className={styles.subtitle}>Drumly Panel Overview & Statistics</p>
          </div>
          <button onClick={loadDashboard} className={styles.refreshButton}>
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {/* Stats Grid */}
        <div className={styles.statsGrid}>
          {stats.map((stat, idx) => (
            <div key={idx} className={styles.statCard}>
              <div className={`${styles.statCardGradient} ${stat.gradientClass}`}></div>
              <div className={styles.statCardContent}>
                <div className={`${styles.statIcon} ${stat.bgClass}`}>
                  <stat.icon size={24} className={stat.iconClass} />
                </div>
                <div className={styles.statInfo}>
                  <div>
                    <h3 className={styles.statValue}>{stat.value}</h3>
                    <p className={styles.statLabel}>{stat.label}</p>
                  </div>
                  {stat.growth && (
                    <div className={styles.statGrowth}>
                      <ArrowUp size={16} />
                      {stat.growth}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Music Statistics */}
        <div>
          <h2 className={styles.sectionTitle}>
            <Music size={24} className={styles.sectionIcon} />
            Music Statistics
          </h2>
          <div className={styles.musicStatsGrid}>
            {musicStats.map((stat, idx) => (
              <div key={idx} className={styles.musicStatCard}>
                <div className={styles.musicStatContent}>
                  <div className={styles.musicStatIcon}>
                    <stat.icon size={24} />
                  </div>
                  <div>
                    <h3 className={styles.musicStatValue}>{stat.value}</h3>
                    <p className={styles.musicStatLabel}>{stat.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent & Popular Songs */}
        <div className={styles.songsGrid}>
          {/* Recent Songs */}
          <div className={styles.songCard}>
            <div className={styles.songCardHeader}>
              <h3 className={styles.songCardTitle}>
                <div className={styles.pulseIndicator}></div>
                Recent Songs
              </h3>
              <span className={styles.songCount}>
                {dashboard.recent_activities.recent_songsv2.length} songs
              </span>
            </div>
            {dashboard.recent_activities.recent_songsv2.length === 0 ? (
              <div className={styles.emptyState}>
                <p className={styles.emptyText}>No recent songs</p>
              </div>
            ) : (
              <div className={styles.songsList}>
                {dashboard.recent_activities.recent_songsv2.map((song: any) => (
                  <div key={song.id} className={styles.songItem}>
                    <div className={styles.songItemContent}>
                      <div className={styles.songInfo}>
                        <h4 className={styles.songName}>{song.name}</h4>
                        <p className={styles.songMeta}>
                          {song.bpm} BPM • {formatDurationMs(song.duration_ms)}
                        </p>
                      </div>
                      <div className={styles.playsBadge}>
                        <span className={styles.playsCount}>{song.play_count}</span>
                        <span className={styles.playsLabel}>plays</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Popular Songs */}
          <div className={styles.songCard}>
            <div className={styles.songCardHeader}>
              <h3 className={styles.songCardTitle}>
                <TrendingUp size={20} className={styles.trendingIcon} />
                Popular Songs
              </h3>
              <span className={styles.songCount}>
                {dashboard.popular_content.popular_songsv2.length} songs
              </span>
            </div>
            {dashboard.popular_content.popular_songsv2.length === 0 ? (
              <div className={styles.emptyState}>
                <p className={styles.emptyText}>No popular songs</p>
              </div>
            ) : (
              <div className={styles.songsList}>
                {dashboard.popular_content.popular_songsv2.map((song: any) => (
                  <div key={song.id} className={styles.songItem}>
                    <div className={styles.songItemContent}>
                      <div className={styles.songInfo}>
                        <h4 className={styles.songName}>{song.name}</h4>
                        <p className={styles.songMeta}>
                          {song.bpm} BPM • {formatDurationMs(song.duration_ms)}
                        </p>
                      </div>
                      <div className={styles.songBadges}>
                        <div className={styles.playsBadge}>
                          <span className={styles.playsCount}>{song.play_count}</span>
                          <span className={styles.playsLabel}>plays</span>
                        </div>
                        <div className={styles.likesBadge}>
                          <Heart size={12} className={styles.likesIcon} />
                          <span className={styles.likesCount}>{song.like_count}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}